import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { subscribeMatches, markMatchSeen } from "../services/matches";
import { SEGMENTS } from "../services/constants";
import { compactNumber, currencySymbol, formatFlatPrice, formatRatePer1k,
  relativeTime } from "../utils/format";
import "./MatchesInbox.css";

// Recent-matches list rendered on the Me dashboard. Handle owner sees
// counterparties (buyers if they're a seller, sellers if they're a
// buyer) whose stock/need aligns with theirs, deep-linked to the
// counterparty profile where Pass 1's pre-filled contact opener kicks in.
//
// Props:
//   handle — the owner handle to subscribe to
//   role   — "seller" | "buyer" — used only for section titling
export default function MatchesInbox({ handle, role = "seller" }) {
  const [matches, setMatches] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!handle) return;
    setReady(false);
    const unsub = subscribeMatches(handle, (list) => {
      setMatches(list); setReady(true);
    });
    return unsub;
  }, [handle]);

  if (!handle) return null;
  const unread = matches.filter((m) => !m.seen).length;
  const title = role === "seller"
      ? "Buyers who match your stock"
      : "Sellers who match your requirement";

  return (
    <section className="matches-inbox">
      <div className="mi-head">
        <h2 className="mi-title">
          {title}
          {unread > 0 && <span className="mi-badge">{unread} new</span>}
        </h2>
        {ready && matches.length > 0 && (
          <span className="mi-count muted">{matches.length} recent</span>
        )}
      </div>

      {ready && matches.length === 0 && (
        <div className="mi-empty muted">
          No matches yet. As {role === "seller" ? "buyers post fresh requirements" : "sellers refresh stock"},
          they'll show up here in real time.
        </div>
      )}

      <div className="mi-list">
        {matches.map((m) => (
          <MatchRow key={m.id} match={m} onOpen={() => markMatchSeen(handle, m.id)} />
        ))}
      </div>
    </section>
  );
}

function MatchRow({ match, onOpen }) {
  const seg = SEGMENTS.find((s) => s.key === match.segment);
  const perK = match.segment === "mix" || match.segment === "premium";
  const symbol = currencySymbol(match.currency || "INR");
  const isSellerCounterparty = match.counterpartyRole === "seller";

  const rate = Number(match.sellerRate || 0);
  const rateStr = rate > 0
      ? (perK ? formatRatePer1k(rate, match.currency)
              : formatFlatPrice(rate, match.currency))
      : null;

  const profileHref = `/${match.counterpartyRole}/${match.counterpartyHandle}`;

  return (
    <Link
      to={profileHref}
      className={`mi-row ${match.seen ? "seen" : "unseen"}`}
      onClick={onOpen}
    >
      <div className="mi-row-head">
        <span className={`segment-pill ${seg?.cssClass || ""}`}>{seg?.label || match.segment}</span>
        <span className="mi-cp-name">@{match.counterpartyHandle}</span>
        {match.counterpartyDisplayName && match.counterpartyDisplayName !== match.counterpartyHandle && (
          <span className="mi-cp-display muted">· {match.counterpartyDisplayName}</span>
        )}
        {isSellerCounterparty && match.counterpartyTier && match.counterpartyTier !== "unranked" && (
          <span className="mi-tier">★{match.counterpartyTier}</span>
        )}
        <span className="mi-when muted">{relativeTime(match.createdAt)}</span>
      </div>

      <div className="mi-row-body">
        {isSellerCounterparty ? (
          <>
            <span>Stock <strong>{compactNumber(match.sellerStock)}</strong></span>
            {rateStr && <span>· from <strong>{rateStr}</strong></span>}
            {match.needQuantity > 0 && (
              <span className="mi-covers">
                · covers {Math.round(Math.min(1, match.sellerStock / match.needQuantity) * 100)}% of your need
              </span>
            )}
          </>
        ) : (
          <>
            <span>Needs <strong>{compactNumber(match.needQuantity)}</strong></span>
            {match.needMaxRate > 0 && (
              <span>· ≤ <strong>{symbol}{Number(match.needMaxRate).toFixed(match.needMaxRate >= 100 ? 0 : 2)}{perK ? "/k" : ""}</strong></span>
            )}
            {match.needUrgency && (
              <span className={`mi-urgency ${match.needUrgency}`}>{String(match.needUrgency).toUpperCase()}</span>
            )}
            {match.counterpartyBudgetINR > 0 && (
              <span className="mi-budget muted">· ₹{compactNumber(match.counterpartyBudgetINR)} budget</span>
            )}
          </>
        )}
      </div>

      <div className="mi-cta">
        Open profile → send a message
      </div>
    </Link>
  );
}
